/**
 * Clerk integration for the /app subtree — and ONLY the /app subtree.
 *
 * The marketing routes ("/", "/waitlist") are pre-rendered and must stay
 * backend-independent, so no <ClerkProvider> ever wraps them (see routes.tsx).
 * Everything Clerk lives here and is mounted per app-family route element.
 *
 * Clerk is MANDATORY: <AppAuthGate> always renders the sign-in gate plus an
 * ACTIVE-ORGANIZATION gate, because org = tenant is the load-bearing isolation
 * boundary. The publishable key comes from VITE_CLERK_PUBLISHABLE_KEY; if it is
 * missing we render a clear inline error rather than crash cryptically (this only
 * wraps /app, never the pre-rendered marketing routes).
 *
 * MAPPING (see backend Clerk token handler): the active Clerk organization is
 * the tenant. A user with no active org is NOT authenticated for tenant-scoped
 * endpoints, so we force org creation/selection before rendering the app.
 */

import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import ConnectionGate from '../ConnectionGate';
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  RedirectToSignIn,
  OrganizationList,
  SignIn,
  SignUp,
  useOrganization,
} from '@clerk/clerk-react';

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

/** Where Clerk should send the user for sign-in / sign-up (client-only routes). */
const SIGN_IN_URL = '/app/sign-in';
const SIGN_UP_URL = '/app/sign-up';
/** Landing target once signed in with an active organization. */
const APP_HOME = '/app';

/** Full-viewport centring shell for the hosted Clerk widgets / messages. */
function AuthScreen({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 16px',
      }}
    >
      {children}
    </div>
  );
}

/**
 * Inline error for a missing publishable key. Clerk cannot mount without it, so
 * rather than let <ClerkProvider> throw a cryptic error we surface exactly what
 * to configure. Only reachable under /app (the marketing routes never mount this).
 */
function MissingKeyNotice() {
  return (
    <AuthScreen>
      <div style={{ maxWidth: 420, textAlign: 'center', color: 'var(--ink, #1a1a1a)' }}>
        <p style={{ fontWeight: 600, marginBottom: 8 }}>
          Clerk publishable key not configured (VITE_CLERK_PUBLISHABLE_KEY)
        </p>
        <p style={{ opacity: 0.7 }}>
          The app cannot authenticate without it. Set VITE_CLERK_PUBLISHABLE_KEY
          (dev uses the committed <code>.env.development</code>; production sets a
          <code> pk_live_</code> key in the Static Web Apps environment).
        </p>
      </div>
    </AuthScreen>
  );
}

/** <ClerkProvider> wired to React Router navigation. Key is guaranteed present here. */
function AppClerkProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY as string}
      routerPush={(to: string) => navigate(to)}
      routerReplace={(to: string) => navigate(to, { replace: true })}
      signInUrl={SIGN_IN_URL}
      signUpUrl={SIGN_UP_URL}
      afterSignOutUrl="/"
    >
      {children}
    </ClerkProvider>
  );
}

/**
 * Active-organization gate. Clerk considers a user signed in with no org
 * selected; for Reeve that user has no tenant, so make them create or pick one
 * (the chosen org becomes their tenant on the next request's token).
 */
function RequireOrganization({ children }: { children: ReactNode }) {
  const { organization, isLoaded } = useOrganization();
  if (!isLoaded) return <AuthScreen>Loading…</AuthScreen>;
  if (!organization) {
    return (
      <AuthScreen>
        <OrganizationList
          hidePersonal
          afterCreateOrganizationUrl={APP_HOME}
          afterSelectOrganizationUrl={APP_HOME}
        />
      </AuthScreen>
    );
  }
  return <>{children}</>;
}

/**
 * Gate for the authenticated app surface: require a session (else redirect to
 * sign-in) AND an active organization. Renders a clear notice if the publishable
 * key is missing.
 */
export function AppAuthGate({ children }: { children: ReactNode }) {
  if (!PUBLISHABLE_KEY) return <MissingKeyNotice />;
  return (
    <AppClerkProvider>
      <SignedIn>
        <RequireOrganization>
          {/*
            Innermost, so it runs only for a signed-in user with an organization. That ordering is
            deliberate: an unreachable backend must not be reported to someone who actually just
            needs to sign in, and the connection screen should not appear before we know who they
            are. Everything below here can assume the API answered at least once.
          */}
          <ConnectionGate>{children}</ConnectionGate>
        </RequireOrganization>
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </AppClerkProvider>
  );
}

/** /app/sign-in — hosted Clerk sign-in. */
export function SignInPage() {
  if (!PUBLISHABLE_KEY) return <MissingKeyNotice />;
  return (
    <AppClerkProvider>
      <AuthScreen>
        <SignIn
          routing="path"
          path={SIGN_IN_URL}
          signUpUrl={SIGN_UP_URL}
          fallbackRedirectUrl={APP_HOME}
        />
      </AuthScreen>
    </AppClerkProvider>
  );
}

/** /app/sign-up — hosted Clerk sign-up. */
export function SignUpPage() {
  if (!PUBLISHABLE_KEY) return <MissingKeyNotice />;
  return (
    <AppClerkProvider>
      <AuthScreen>
        <SignUp
          routing="path"
          path={SIGN_UP_URL}
          signInUrl={SIGN_IN_URL}
          fallbackRedirectUrl={APP_HOME}
        />
      </AuthScreen>
    </AppClerkProvider>
  );
}
